// SPDX-License-Identifier: MIT
// Récupère les UsageEvent que claude-stream.ts laisse tomber (rate_limit_event +
// usage de la ligne `result`). nowMs est INJECTÉ (timestamp d'arrivée de la ligne).
import type { UsageEvent } from './usage-types';

interface Json { [k: string]: unknown }
function obj(v: unknown): Json | null { return v && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : null; }
function num(v: unknown): number { return typeof v === 'number' ? v : 0; }
function str(v: unknown): string { return typeof v === 'string' ? v : ''; }

export function parseUsageFromStreamLine(line: string, nowMs: number): UsageEvent | null {
  const t = line.trim();
  if (!t) return null;
  let o: Json | null;
  try { o = obj(JSON.parse(t)); } catch { return null; }
  if (!o) return null;

  if (o.type === 'rate_limit_event') {
    const info = obj(o.rate_limit_info);
    if (!info || typeof info.resetsAt !== 'number') return null;
    return {
      kind: 'rate_limit',
      status: info.status === 'throttled' ? 'throttled' : 'allowed',
      resetsAt: info.resetsAt,
      rateLimitType: str(info.rateLimitType) || 'five_hour',
      overageRejected: info.overageStatus === 'rejected',
    };
  }

  if (o.type === 'result') {
    const u = obj(o.usage);
    if (!u) return null;
    return {
      kind: 'usage',
      timestampMs: nowMs,
      inputTokens: num(u.input_tokens),
      cacheCreationTokens: num(u.cache_creation_input_tokens),
      cacheReadTokens: num(u.cache_read_input_tokens),
      outputTokens: num(u.output_tokens),
      model: Object.keys(obj(o.modelUsage) ?? {})[0] ?? '',
    };
  }
  return null;
}

// Web : transforme les en-têtes anthropic-ratelimit-* (renvoyés par l'edge-fn
// dans la réponse JSON sous la clé `rateLimit`) en UsageEvent web_headers.
// `reset` = ISO-8601 string ou epoch-s selon la version de l'API Anthropic.
export function parseUsageFromHeaders(h: Record<string, string>): UsageEvent | null {
  const status = h['anthropic-ratelimit-unified-status'];
  const reset = h['anthropic-ratelimit-unified-reset'] ?? h['anthropic-ratelimit-requests-reset'];
  if (!status && !reset) return null;
  let resetsAt: number | null = null;
  if (reset) {
    const asNum = Number(reset);
    resetsAt = Number.isFinite(asNum) && asNum > 1_000_000_000 ? asNum : Math.floor(Date.parse(reset) / 1000) || null;
  }
  return {
    kind: 'web_headers',
    status: status === 'throttled' || status === 'rejected' ? 'throttled' : 'allowed',
    resetsAt,
  };
}
